import { z } from "zod"
import { generatedStateNames } from "../../../../packages/contracts/openapi/generated/state-contracts.ts"
import { viewStates } from "./routes.ts"
import { ownedRedemptionLifecycleResponseSchema, phoneVerificationStatusSchema, redemptionCheckoutReadinessSchema } from "./todo26-contract.ts"

const viewStateSchema = z.enum(viewStates)
export const failureCauses = ["EXPIRED_ENTRY", "QUOTE_EXPIRED", "INSUFFICIENT_BALANCE", "MMS_DELIVERY_FAILED", "DELETION_BLOCKED"] as const
export type FailureCause = (typeof failureCauses)[number]
const detailSchema = z.strictObject({ label: z.string().min(1), value: z.string().min(1) })
const baseSchema = z.strictObject({ description: z.string(), failureCause: z.enum(failureCauses).optional(), heading: z.string().min(1), state: viewStateSchema })

const activitySchema = z.strictObject({ amount: z.string().min(1), id: z.uuid(), label: z.string().min(1), occurredAt: z.iso.datetime(), sourceMediaName: z.string().min(1).nullish(), status: z.string().min(1).default("COMPLETED") })
const pageViewSchema = baseSchema.extend({ details: z.array(detailSchema), entries: z.array(activitySchema).optional(), items: z.array(z.strictObject({ id: z.string().min(1), label: z.string().min(1), meta: z.string() })), kind: z.literal("page"), phoneVerification: phoneVerificationStatusSchema.optional() })
const historyViewSchema = baseSchema.extend({ details: z.array(detailSchema).optional(), entries: z.array(activitySchema), kind: z.literal("history") })
const promoBannerSchema = z
  .strictObject({ ctaLabel: z.string().min(1), href: z.string().min(1), kicker: z.string().min(1), title: z.string().min(1) })
  .nullable()
  .optional()
const productSchema = z.strictObject({ available: z.boolean(), brandName: z.string().min(1).optional(), category: z.string(), id: z.uuid(), imageUrl: z.url(), name: z.string().min(1), price: z.string().min(1), priceWon: z.number().int().positive() })
const homeViewSchema = baseSchema.extend({
  details: z.array(detailSchema),
  entries: z.array(activitySchema).max(5),
  featuredProducts: z.array(productSchema).max(8).optional().default([]),
  kind: z.literal("home"),
  promoBanner: promoBannerSchema,
})
const catalogViewSchema = baseSchema.extend({ kind: z.literal("catalog"), availableWon: z.number().int().nonnegative().optional(), products: z.array(productSchema), promoBanner: promoBannerSchema })
const productViewSchema = baseSchema.extend({ checkoutReadiness: redemptionCheckoutReadinessSchema.optional(), kind: z.literal("product"), product: productSchema, quoteId: z.uuid().optional() })
const transactionViewSchema = baseSchema.extend({
  copyKey: z.string().min(1),
  details: z.array(detailSchema),
  domain: z.enum(["conversion", "redemption"]),
  kind: z.literal("transaction"),
  productImageUrl: z.url().optional(),
  reference: z.string().min(1),
  serverState: z.union([z.enum(generatedStateNames.conversion), z.enum(generatedStateNames.redemption)]),
  checkoutReadiness: redemptionCheckoutReadinessSchema.optional(),
  todo26Lifecycle: ownedRedemptionLifecycleResponseSchema.optional(),
})
const voucherViewSchema = baseSchema.extend({ expiresAt: z.iso.datetime(), kind: z.literal("voucher"), orderId: z.uuid(), recipientMask: z.string().regex(/^01\d-\*{4}-\d{4}$/), state: viewStateSchema, serverState: z.enum(generatedStateNames.redemption) })
const notificationsViewSchema = baseSchema.extend({
  kind: z.literal("notifications"),
  notifications: z.array(
    z.strictObject({
      body: z.string().min(1),
      destination: z.enum(["balance", "notifications", "orders", "support", "vouchers"]).nullable().optional().default(null),
      destinationReferenceId: z.uuid().nullable().optional().default(null),
      id: z.uuid(),
      occurredAt: z.iso.datetime(),
      read: z.boolean(),
      title: z.string().min(1),
    }),
  ),
})
const preferencesViewSchema = baseSchema.extend({ kind: z.literal("preferences"), preferences: z.strictObject({ marketing: z.boolean(), service: z.boolean() }) })
const supportMessageSchema = z.strictObject({ author: z.enum(["USER", "ADMIN", "SYSTEM"]), body: z.string().min(1), createdAt: z.iso.datetime(), messageId: z.uuid() })
const supportCaseSchema = z.strictObject({ id: z.uuid(), message: z.string().min(1), messages: z.array(supportMessageSchema).default([]), status: z.enum(["RECEIVED", "ANSWERED"]), subject: z.string().min(1) })
const supportViewSchema = baseSchema.extend({ cases: z.array(supportCaseSchema), kind: z.literal("support"), selectedCase: supportCaseSchema.optional() })
const commerceViewSchema = baseSchema.extend({
  items: z.array(z.strictObject({ amount: z.string().min(1), expiresAt: z.iso.datetime().nullable(), id: z.uuid(), label: z.string().min(1), occurredAt: z.iso.datetime(), status: z.string().min(1) })),
  kind: z.literal("commerce"),
})

export const userViewSchema = z.discriminatedUnion("kind", [pageViewSchema, homeViewSchema, historyViewSchema, catalogViewSchema, productViewSchema, transactionViewSchema, voucherViewSchema, commerceViewSchema, notificationsViewSchema, preferencesViewSchema, supportViewSchema])
export type UserView = z.infer<typeof userViewSchema>

type TransactionView = Extract<UserView, { readonly kind: "transaction" }>

export const consumerTransactionDetails = (view: TransactionView): TransactionView["details"] => (view.domain === "conversion" ? view.details.filter(({ label }) => label === "원천 포인트" || label === "최종 적립") : view.details.filter(({ label }) => label !== "수수료"))
